"use client";

import { c as _c } from "react/compiler-runtime";
import { Typography } from "@repo/ui/aops-components/typography";
import { useId, useState, type ReactNode } from "react";
import styles from "./CourseLevelSection.module.css";
import { cn } from "@repo/ui/lib/utils";
import { CourseTag } from "@/app/components/CourseLevelPage/CourseTag/CourseTag";
import { SelectableTile } from "@/app/components/CourseLevelPage/SelectableTile";
export type CourseLevelSectionProps = {
  title: string;
  subtitle: string;
  stepTag?: string;
  options: Array<{
    body: ReactNode;
    value: string;
    disabled?: boolean;
  }>;
  onSelect?: (value: string) => void;
  /** Initially selected option `value`, if present in `options` */
  defaultValue?: string | null;
  /**
   * Rendered after the content-width body, as a direct child of the section.
   */
  footer?: ReactNode;
  /** Shared SelectableTile subgrid row count across columns */
  tileRows?: number;
  /** Optional fragment id for deep links (e.g. #formats, #sequence) */
  id?: string;
};

/**
 *
 * @param title - The title of the section
 * @param subtitle - The subtitle of the section
 * @param options - The options to display
 * @param onSelect - The function to call when an option is selected
 * @param defaultValue - Optional initially selected option value
 * @param footer - Optional full-bleed content after the body
 * @param tileRows - Optional shared subgrid row count for the tile list
 * @returns
 */
export function CourseLevelSection(t0) {
  const $ = _c(37);
  const {
    title,
    subtitle,
    stepTag,
    options,
    onSelect,
    defaultValue,
    footer,
    tileRows,
    id
  } = t0;
  const titleId = useId();
  const bodyId = useId();
  let t1;
  if ($[0] !== defaultValue || $[1] !== options) {
    t1 = () => {
      if (defaultValue == null) {
        return null;
      }
      const index = options.findIndex(item => item.value === defaultValue);
      return index >= 0 ? index : null;
    };
    $[0] = defaultValue;
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [selectedIndex, setSelectedIndex] = useState(t1);
  let t2;
  if ($[3] !== stepTag) {
    t2 = stepTag && <CourseTag>{stepTag}</CourseTag>;
    $[3] = stepTag;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== title || $[6] !== titleId) {
    t3 = <Typography id={titleId} as="h2" variant="h4">{title}</Typography>;
    $[5] = title;
    $[6] = titleId;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== subtitle) {
    t4 = <Typography variant="body">{subtitle}</Typography>;
    $[8] = subtitle;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <div className={styles.header}>{t2}{t3}{t4}</div>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const t6 = footer && styles.bodyWithFooter;
  let t7;
  if ($[14] !== t6) {
    t7 = cn(styles.body, t6);
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== onSelect || $[17] !== options || $[18] !== selectedIndex) {
    let t9;
    if ($[20] !== onSelect || $[21] !== selectedIndex) {
      t9 = (item_0, index_0) => {
        const isSelected = selectedIndex === index_0;
        const isCardSelectable = Boolean(onSelect) && !item_0.disabled;
        const pick = () => {
          if (!onSelect || item_0.disabled) {
            return;
          }
          setSelectedIndex(index_0);
          onSelect(item_0.value);
        };
        return <div key={item_0.value} role={isCardSelectable ? "button" : undefined} className={styles.optionCard} aria-pressed={isCardSelectable ? isSelected : undefined} aria-disabled={item_0.disabled} data-disabled={item_0.disabled || undefined} data-selectable={isCardSelectable || undefined} onClick={isCardSelectable ? pick : undefined}><div className={styles.optionCardDescription}>{item_0.body}</div></div>;
      };
      $[20] = onSelect;
      $[21] = selectedIndex;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    t8 = options.map(t9);
    $[16] = onSelect;
    $[17] = options;
    $[18] = selectedIndex;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[23] !== t8 || $[24] !== tileRows) {
    t9 = <SelectableTile.List rows={tileRows}>{t8}</SelectableTile.List>;
    $[23] = t8;
    $[24] = tileRows;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== bodyId || $[27] !== t7 || $[28] !== t9) {
    t10 = <div id={bodyId} className={t7}>{t9}</div>;
    $[26] = bodyId;
    $[27] = t7;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== footer || $[31] !== id || $[32] !== t10 || $[33] !== t5 || $[34] !== title || $[35] !== titleId) {
    t11 = <section id={id} className={styles.section} aria-label={title} aria-labelledby={titleId}>{t5}{t10}{footer}</section>;
    $[30] = footer;
    $[31] = id;
    $[32] = t10;
    $[33] = t5;
    $[34] = title;
    $[35] = titleId;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  return t11;
}